/** Small 3D vector and rotation helpers for the Ember sprite generator. Plain tuples, no classes. */
export type Vec3 = [number, number, number]
/** Row-major 3x3 rotation matrix. */
export type Mat3 = [number, number, number, number, number, number, number, number, number]

export const v3 = (x: number, y: number, z: number): Vec3 => [x, y, z]
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s]
export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
export const cross = (a: Vec3, b: Vec3): Vec3 => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
]
export const length = (a: Vec3): number => Math.sqrt(dot(a, a))
export const normalize = (a: Vec3): Vec3 => {
    const l = length(a)
    return l > 1e-9 ? scale(a, 1 / l) : [0, 0, 0]
}
export const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
]
export const distance = (a: Vec3, b: Vec3): number => length(sub(a, b))

export const IDENTITY: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1]

export const mul = (a: Mat3, b: Mat3): Mat3 => {
    const r = new Array(9) as Mat3
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j]
        }
    }
    return r
}

export const apply = (m: Mat3, v: Vec3): Vec3 => [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
]

export const transpose = (m: Mat3): Mat3 => [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]

/** Rotation about +x: positive tips +y toward +z (a forward lean when +z is forward). */
export const rotX = (a: number): Mat3 => {
    const c = Math.cos(a), s = Math.sin(a)
    return [1, 0, 0, 0, c, -s, 0, s, c]
}

/** Rotation about +y: positive turns +z toward +x. */
export const rotY = (a: number): Mat3 => {
    const c = Math.cos(a), s = Math.sin(a)
    return [c, 0, s, 0, 1, 0, -s, 0, c]
}

/** Rotation about +z: positive turns +x toward +y. */
export const rotZ = (a: number): Mat3 => {
    const c = Math.cos(a), s = Math.sin(a)
    return [c, -s, 0, s, c, 0, 0, 0, 1]
}

export const columns = (m: Mat3): [Vec3, Vec3, Vec3] => [
    [m[0], m[3], m[6]],
    [m[1], m[4], m[7]],
    [m[2], m[5], m[8]],
]

export const deg = (d: number): number => (d * Math.PI) / 180

export const smoothstep = (t: number): number => {
    const x = Math.max(0, Math.min(1, t))
    return x * x * (3 - 2 * x)
}

/** Half-cosine ease, 0 -> 1. */
export const ease = (t: number): number => (1 - Math.cos(Math.PI * Math.max(0, Math.min(1, t)))) / 2
